import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Pain subsection adapter
 * Handles pain assessment including FLACC scale components (7 fields)
 */
export class PainAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Pain assessment fields
    const painStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS
    )?.at(0)

    const painGoal = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL
    )?.at(0)

    // FLACC Scale components (Face, Legs, Activity, Cry, Consolability)
    const flaccScaleFace = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE
    )?.at(0)

    const flaccScaleLegs = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS
    )?.at(0)

    const flaccScaleActivity = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY
    )?.at(0)

    const flaccScaleCry = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY
    )?.at(0)

    const flaccScaleConsolability = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.PAIN_STATUS]: painStatus,
      [RecertFormField.PAIN_GOAL]: painGoal,
      [RecertFormField.PAIN_FLACC_SCALE_FACE]: flaccScaleFace,
      [RecertFormField.PAIN_FLACC_SCALE_LEGS]: flaccScaleLegs,
      [RecertFormField.PAIN_FLACC_SCALE_ACTIVITY]: flaccScaleActivity,
      [RecertFormField.PAIN_FLACC_SCALE_CRY]: flaccScaleCry,
      [RecertFormField.PAIN_FLACC_SCALE_CONSOLABILITY]: flaccScaleConsolability,
      [RecertFormField.PAIN_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.PAIN_STATUS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS,
        value: this.getFormDataField(RecertFormField.PAIN_STATUS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_GOAL)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL,
        value: this.getFormDataField(RecertFormField.PAIN_GOAL)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_FLACC_SCALE_FACE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE,
        value: this.getFormDataField(RecertFormField.PAIN_FLACC_SCALE_FACE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_FLACC_SCALE_LEGS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS,
        value: this.getFormDataField(RecertFormField.PAIN_FLACC_SCALE_LEGS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_FLACC_SCALE_ACTIVITY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY,
        value: this.getFormDataField(RecertFormField.PAIN_FLACC_SCALE_ACTIVITY)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_FLACC_SCALE_CRY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY,
        value: this.getFormDataField(RecertFormField.PAIN_FLACC_SCALE_CRY)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_FLACC_SCALE_CONSOLABILITY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY,
        value: this.getFormDataField(RecertFormField.PAIN_FLACC_SCALE_CONSOLABILITY)
      })
    }

    if (this.formDataFieldExists(RecertFormField.PAIN_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.PAIN_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
